import { useParams, Link, Navigate, useSearchParams } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import { Card, EmptyState, PageContainer, PageHeader } from '@/components/ui';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { traduzirLinkComercialV1 } from '@/lib/crm/crm-legacy-redirect';
import { ManutencaoPage } from './manutencao/ManutencaoPage';
// Projetos 2.0 F2.1: kanban Compras/Financeiro, gated por flag.
import { useMyFeatureFlag } from '@/lib/feature-flags-store';
import { useAcessoDaTela } from '@/lib/acesso-store';
import { WorkflowKanbanPage } from './WorkflowKanbanPage';
import { FINANCEIRO_STAGES } from './workflow-kanban-stages';
// Round D / D2: cotação multi-fornecedor do módulo de Compras.
import { ComprasCotacoesPage } from './compras/ComprasCotacoesPage';
// Round D / D5: solicitação de compra interna.
import { SolicitacaoCompraPage } from './compras/SolicitacaoCompraPage';
// Fluxo unificado / FU-2: quadro de Compras (KanbanBoard universal, card-espelho).
import { ComprasQuadroPage } from './compras/ComprasQuadroPage';
// MONITORAMENTO fase 1 (mig 515): Manutenção › Monitoramento, atrás da flag.
import MonitoramentoPage from '@/pages/manutencao/MonitoramentoPage';
// QUATRO-FRENTES (04/09/2026): Manutencao > Orcamentos — a fila do que orcar.
import OrcamentosPage from '@/pages/manutencao/OrcamentosPage';
import ChamadosPage from '@/pages/manutencao/ChamadosPage';

const DEPARTAMENTOS: Record<string, { titulo: string; descricao: string }> = {
  comercial: {
    titulo: 'Comercial',
    descricao: 'Fluxo de vendas, propostas, follow-ups e gestão de oportunidades.',
  },
  manutencao: {
    titulo: 'Manutenção',
    descricao: 'OS, preventivas, atendimentos técnicos e contratos ativos.',
  },
  financeiro: {
    // FASE-C (copy): "(integração Omie em R6)" era código de rodada vazando
    // pra UI — descrição aparece no kanban do Financeiro (WorkflowKanbanPage).
    titulo: 'Financeiro',
    descricao: 'Contas a pagar/receber, contratos e faturas.',
  },
  compras: {
    titulo: 'Compras',
    descricao: 'Cotações multi-fornecedor, solicitações de compra e fornecedores.',
  },
};

// FASE-C ("se não funciona, ou termina ou some"): Clientes e Projetos NUNCA
// tiveram tela em /menu/* — caíam no aviso "Placeholder. Implementação real
// virá…". As telas reais existem em /admin/*, então o endereço antigo passa a
// levar direto pra elas (links e favoritos antigos continuam funcionando).
// RH saiu do mapa: não existe tela nem menu — vira "não existe".
const DESTINOS_REAIS: Record<string, string> = {
  clientes: '/admin/clientes-v2',
  projetos: '/admin/projetos',
};

/**
 * Tela operacional de cada departamento.
 *
 * URL: /menu/:departamento
 *
 * Manutenção e Compras têm tela própria aqui; Comercial, Clientes e Projetos
 * redirecionam pro endereço real. Departamento sem tela liberada mostra um
 * aviso honesto (nada de "placeholder" nem código de rodada na tela).
 */
/**
 * A entrada da Manutenção leva à Agenda (quem tem a OS) ou aos Chamados. ⚠️ Espera as permissões carregarem antes de
 * decidir: carregando, "pode" responde não, e o admin cairia nos Chamados. Componente próprio para só pedir as
 * permissões nesta entrada (o resto da página não depende delas).
 */
function EntradaDaManutencao() {
  const acessoOs = useAcessoDaTela('manutencao.os.ver');
  if (acessoOs.carregando) return null;
  return <Navigate to={acessoOs.permitido ? '/menu/manutencao/agenda' : '/menu/manutencao/chamados-auvo'} replace />;
}

export function MenuPage() {
  const { departamento = '', workflow } = useParams();
  // F2.1: hooks ANTES de qualquer return (regras do React).
  const financeiroOn = useMyFeatureFlag('FINANCEIRO_WORKFLOW_ENABLED');
  // Round D / D2: módulo de Compras (cotação) em /menu/compras/cotacoes.
  const comprasModuleOn = useMyFeatureFlag('COMPRAS_ENABLED');
  // MONITORAMENTO fase 1: com a flag OFF a sub-rota simplesmente não existe,
  // e /menu/manutencao/monitoramento cai na tela da Manutenção — nenhum
  // usuário vê diferença, que é como esta rodada vai ao ar.
  const monitoramentoOn = useMyFeatureFlag('MONITORAMENTO_ENABLED');
  const [searchParams] = useSearchParams();
  const dept = DEPARTAMENTOS[departamento];

  // B6 (AJUDA-UX-QA): a Ferramenta de Orçamento foi pra CASA NOVA no CRM v2 —
  // a página v1 foi APOSENTADA. /menu/comercial/orcamentos redireciona pra
  // lista v2 preservando a query (?open=<id> abre o construtor lá). O gate
  // ORCAMENTO_ENABLED (com loading, padrão A8) agora vive na lista v2.
  if (departamento === 'comercial' && workflow === 'orcamentos') {
    const qs = searchParams.toString();
    return <Navigate to={`/admin/comercial/orcamentos${qs ? `?${qs}` : ''}`} replace />;
  }

  // Fase E3 (exclusão do comercial v1): a ComercialPage foi REMOVIDA — toda
  // entrada em /menu/comercial redireciona pro Funil v2 traduzindo deep-links
  // (?openDeal=/?openCard= → card migrado da C8), incondicionalmente.
  if (departamento === 'comercial') {
    return <Navigate to={traduzirLinkComercialV1(searchParams)} replace />;
  }

  // FASE-C: endereços que só existiam pra mostrar aviso agora levam pra tela real.
  if (DESTINOS_REAIS[departamento]) {
    return <Navigate to={DESTINOS_REAIS[departamento]} replace />;
  }

  // FASE-C: departamento inexistente cai no 404 canônico do app (era uma
  // segunda tela de "não existe", com layout próprio, só pra este caso).
  if (!dept) {
    return <NotFoundPage />;
  }

  // R5: Manutenção tem implementação real
  // 🔴 UM ÚNICO item de menu: as abas "Ao vivo" e "Indicadores" vivem DENTRO
  // desta tela (query string `?aba=`), não como rotas irmãs.
  if (departamento === 'manutencao' && workflow === 'chamados-auvo') {
    return (
      <AppShell>
        <ChamadosPage />
      </AppShell>
    );
  }

  if (departamento === 'manutencao' && workflow === 'orcamentos') {
    return (
      <AppShell>
        <OrcamentosPage />
      </AppShell>
    );
  }

  if (departamento === 'manutencao' && workflow === 'monitoramento' && monitoramentoOn) {
    return (
      <AppShell>
        <MonitoramentoPage />
      </AppShell>
    );
  }

  // Achado 12 / F3 (Matheus, 28/10): a entrada da Manutenção (o botão "Agenda" do celular, o item do menu) leva à Agenda
  // para quem tem a OS; quem não tem cai nos Chamados. ⛔ Os quadros Preventiva e Vistoria saíram (mig 802); a Corretiva
  // continua em `/menu/manutencao/corretiva` (é onde caem os chamados do Portal).
  if (departamento === 'manutencao' && (!workflow || workflow === 'preventiva' || workflow === 'vistoria')) {
    return <EntradaDaManutencao />;
  }

  if (departamento === 'manutencao') {
    return (
      <AppShell>
        {/* LAYOUT-CANONICO: cada return desta tela tinha o seu `px-4 md:px-8
            py-6 md:py-10 w-full` — todos viraram a régua única. Aqui é `full`
            porque o quadro de OSs rola na horizontal (sem `alturaCheia`: o
            kanban da Manutenção não rola dentro de si). */}
        <PageContainer variante="full">
          <ManutencaoPage />
        </PageContainer>
      </AppShell>
    );
  }

  // Fluxo unificado / FU-2: sub-rota "Quadro" (/menu/compras/quadro) — o kanban
  // unificado de Compras (card-espelho da solicitação). Gated por COMPRAS_ENABLED.
  if (departamento === 'compras' && workflow === 'quadro' && comprasModuleOn) {
    return (
      <AppShell>
        <ComprasQuadroPage />
      </AppShell>
    );
  }

  // Round D / D2: sub-rota "Cotações" do módulo de Compras (/menu/compras/cotacoes).
  // Gated por COMPRAS_ENABLED (independe do kanban COMPRAS_WORKFLOW_ENABLED).
  if (departamento === 'compras' && workflow === 'cotacoes' && comprasModuleOn) {
    return (
      <AppShell>
        {/* LAYOUT-CANONICO: wrapper próprio → régua única (variante padrão:
            Cotações é conteúdo de leitura, não quadro que rola na horizontal). */}
        <PageContainer>
          <ComprasCotacoesPage />
        </PageContainer>
      </AppShell>
    );
  }

  // Round D / D5: sub-rota "Solicitar compra" (/menu/compras/solicitar). Gated por
  // COMPRAS_ENABLED. A tela em si é pra qualquer colaborador (backend requireAuth).
  if (departamento === 'compras' && workflow === 'solicitar' && comprasModuleOn) {
    return (
      <AppShell>
        {/* LAYOUT-CANONICO: wrapper próprio → régua única. */}
        <PageContainer>
          <SolicitacaoCompraPage />
        </PageContainer>
      </AppShell>
    );
  }

  // FU-6 (fluxo unificado): o **Quadro** unificado SUBSTITUI o kanban antigo de
  // Compras. `/menu/compras` (sem sub-rota) abre o Quadro. COMPRAS_WORKFLOW_ENABLED
  // não rege mais o Compras (era o kanban antigo, aposentado).
  if (departamento === 'compras' && comprasModuleOn) {
    return (
      <AppShell>
        <ComprasQuadroPage />
      </AppShell>
    );
  }
  // F2.1: Financeiro como kanban universal — só com a flag ON.
  if (departamento === 'financeiro' && financeiroOn) {
    return (
      <AppShell>
        {/* LAYOUT-CANONICO: wrapper próprio → régua única. Kanban = `full` +
            `alturaCheia` (o quadro rola dentro da altura da janela). */}
        <PageContainer variante="full" alturaCheia>
          <WorkflowKanbanPage
            workflowSlug="financeiro"
            titulo={dept.titulo}
            descricao={dept.descricao}
            defaultStages={FINANCEIRO_STAGES}
          />
        </PageContainer>
      </AppShell>
    );
  }

  // FASE-C: o que sobra aqui é Compras/Financeiro com o módulo ainda desligado
  // pro usuário. Era a tela "Placeholder. Implementação real virá em rodadas
  // específicas conforme roadmap" + "URL atual: /menu/…" + "R2/R5/R6" (jargão
  // de handoff na cara do usuário). Agora é um aviso honesto, sem código de
  // rodada e sem promessa de data.
  return (
    <AppShell>
      {/* LAYOUT-CANONICO: o `max-w-3xl` próprio virou a régua única. */}
      <PageContainer>
        <PageHeader crumb="Operacional" title={dept.titulo} subtitle={dept.descricao} />
        <Card variant="flat" data-testid="menu-modulo-indisponivel">
          <EmptyState
            title="Módulo ainda não liberado"
            description={`${dept.titulo} está desligado para o seu acesso. Peça a liberação ao administrador do sistema — nada precisa ser feito da sua parte.`}
            action={
              <Link
                to="/"
                className="inline-flex h-10 items-center rounded-pill bg-red px-5 text-sm font-medium text-white transition-colors hover:bg-red-deep"
              >
                Voltar para a Home
              </Link>
            }
          />
        </Card>
      </PageContainer>
    </AppShell>
  );
}
